/**
 * Minimal, dependency-free semver comparison — just enough for changelog
 * gating (is release X newer than the last version the user saw?).
 *
 * Parses `x.y.z` numerically and is tolerant of missing/garbage parts
 * (missing → 0, non-numeric → 0), so partial versions like `1.2` or an empty
 * "last seen" string compare sensibly rather than throwing.
 */

/** Parses a version string into a fixed [major, minor, patch] numeric tuple. */
function parse(version: string): [number, number, number] {
  const parts = (version ?? '').split('.')
  const num = (i: number): number => {
    const n = parseInt(parts[i] ?? '', 10)
    return Number.isFinite(n) ? n : 0
  }
  return [num(0), num(1), num(2)]
}

/**
 * Returns a negative number if a < b, positive if a > b, 0 if equal —
 * suitable for Array.prototype.sort.
 */
export function compareSemver(a: string, b: string): number {
  const av = parse(a)
  const bv = parse(b)
  for (let i = 0; i < 3; i++) {
    if (av[i] !== bv[i]) return av[i] - bv[i]
  }
  return 0
}

/** True when version `a` is strictly newer than version `b`. */
export function semverGt(a: string, b: string): boolean {
  return compareSemver(a, b) > 0
}
